/* eslint-disable react/no-danger */
import {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import type { ReactNode, Ref } from "react";
import { Box, Text } from "@mantine/core";
import RichTextDiagnosis, {
  DiagnosisMacroItem,
  VariableConfig,
} from "./RichTextDiagnosis";
import { isEmptyHtml } from "../../utils/helpers";
import { resolveVariableChipValues } from "../shared/tiptap/VariableChip";
import {
  CLINICAL_RICH_TEXT_CLASS,
  CLINICAL_RICH_TEXT_STYLES,
} from "../../utils/clinicalRichText";

export interface EditableSectionHandle {
  open: () => void;
}

interface EditableSectionProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isReadOnly?: boolean;
  /** When true, strips top margin from first paragraph so it aligns inline with adjacent elements */
  inlineStart?: boolean;
  /** Called when a macro is applied — gives parent a chance to sync micro desc etc. */
  onMacroApplied?: (macro: DiagnosisMacroItem) => void;
  /** Variable chip config — passed to RichTextDiagnosis when editing */
  variableConfig?: VariableConfig;
  /** Optional node rendered as a header row inside the editor box (above the toolbar) when editing */
  codeInput?: ReactNode;
  /** When true, the code input has no value — editor auto-focus is suppressed so the code input can receive focus */
  codeIsEmpty?: boolean;
  /** Called when the section enters editing mode (click or programmatic open) */
  onEditingStart?: () => void;
  /** Optional override for the rendered body text style (read-only & preview modes). Defaults to 13px / 1.6 line-height. */
  textStyle?: React.CSSProperties;
  /** Preserve authored spaces and empty paragraphs in reportable clinical text. */
  preserveRichTextLayout?: boolean;
}

/**
 * Replace {{VAR}} tokens (both bare and wrapped in <span data-variable="VAR">)
 * with their resolved values from variableConfig for collapsed/read-only display.
 * Unresolved variables are rendered as a small styled badge.
 */
const UNRESOLVED_BADGE =
  "background:var(--mantine-color-orange-1);color:var(--mantine-color-orange-8);" +
  "padding:1px 5px;border-radius:3px;font-family:monospace;font-size:0.85em";

function resolveVariablesForDisplay(
  html: string,
  variableConfig?: VariableConfig
): string {
  if (!html) return html;

  // Build extra vars from variableConfig for system/context variables
  const extraVars: Record<string, string | null | undefined> = {};
  if (variableConfig) {
    Object.entries(variableConfig).forEach(([key, cfg]) => {
      extraVars[key] = cfg.value;
    });
  }

  // First pass: resolve data-variable-value attrs and extraVars; remaining chips
  // stay as-is for the badge pass below.
  let result = resolveVariableChipValues(html, extraVars);

  // Replace any remaining <span data-variable> chips that weren't resolved as badges
  result = result.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/g,
    (_match, varName: string) => {
      return `<span style="${UNRESOLVED_BADGE}">{{${varName}}}</span>`;
    }
  );

  // Replace any remaining bare {{VAR}} tokens
  result = result.replace(/\{\{(\w+)\}\}/g, (_match, varName: string) => {
    return `<span style="${UNRESOLVED_BADGE}">{{${varName}}}</span>`;
  });

  return result;
}

const INLINE_STRIP_STYLE = `
  .es-inline-strip p:first-child,
  .es-inline-strip p:first-of-type {
    margin-top: 0 !important;
    margin-block-start: 0 !important;
    padding-top: 0 !important;
  }
`;

const COMPACT_STYLE = `
  .es-compact p {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-block-start: 0 !important;
    margin-block-end: 0 !important;
  }
  .es-compact ul, .es-compact ol {
    margin-top: 2px !important;
    margin-bottom: 2px !important;
  }
`;

export const EditableSection = forwardRef(function EditableSection(
  {
    value,
    onChange,
    placeholder = "Click to edit...",
    isReadOnly = false,
    inlineStart = false,
    onMacroApplied,
    variableConfig,
    codeInput,
    codeIsEmpty = false,
    onEditingStart,
    textStyle,
    preserveRichTextLayout = false,
  }: EditableSectionProps,
  ref: Ref<EditableSectionHandle>
) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasContent = !isEmptyHtml(value);
  // Pagination can isolate an authored empty paragraph into its own fragment.
  const hasAuthoredLayout = preserveRichTextLayout && value.trim().length > 0;

  useImperativeHandle(ref, () => ({
    open: () => {
      if (!isReadOnly) {
        setIsEditing(true);
        if (codeIsEmpty) onEditingStart?.();
      }
    },
  }));

  const handleClick = useCallback(() => {
    if (!isReadOnly) {
      setIsEditing(true);
      if (codeIsEmpty) onEditingStart?.();
    }
  }, [isReadOnly, codeIsEmpty, onEditingStart]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const related = e.relatedTarget as HTMLElement;
    if (
      related?.closest?.(".mantine-Menu-dropdown") ||
      related?.closest?.(".mantine-Popover-dropdown") ||
      related?.closest?.("[data-text-expansion-popup]")
    ) {
      return;
    }
    if (!containerRef.current?.contains(related)) {
      setIsEditing(false);
    }
  }, []);

  const inlineClass = inlineStart ? "es-inline-strip" : "";
  const richTextClass = preserveRichTextLayout ? CLINICAL_RICH_TEXT_CLASS : "";

  const displayHtml = resolveVariablesForDisplay(value, variableConfig);

  if (isReadOnly) {
    if (!hasContent && !hasAuthoredLayout) return null;
    return (
      <Box className={`es-compact ${inlineClass} ${richTextClass}`}>
        <style>{COMPACT_STYLE}</style>
        {preserveRichTextLayout && <style>{CLINICAL_RICH_TEXT_STYLES}</style>}
        {inlineStart && <style>{INLINE_STRIP_STYLE}</style>}
        <div
          style={{ fontSize: 13, lineHeight: 1.6, ...textStyle }}
          dangerouslySetInnerHTML={{ __html: displayHtml }}
        />
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box ref={containerRef} onBlur={handleBlur}>
        <RichTextDiagnosis
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          minHeight={100}
          autoFocus
          suppressEditorFocus={codeIsEmpty}
          onMacroApplied={onMacroApplied}
          variableConfig={variableConfig}
          codeInput={codeInput}
        />
      </Box>
    );
  }

  // Preview mode — no border, just subtle hover highlight (original style)
  return (
    <Box
      className={`es-compact ${inlineClass} ${richTextClass}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      style={{
        borderRadius: 3,
        padding: inlineStart ? 0 : "0 4px",
        marginLeft: inlineStart ? 0 : -4,
        cursor: "text",
        backgroundColor: isHovered ? "rgba(0, 100, 255, 0.04)" : "transparent",
        outline: isHovered ? "1px dashed var(--mantine-color-gray-4)" : "none",
        transition: "background-color 0.1s ease, outline 0.1s ease",
        minHeight: 24,
      }}
    >
      <style>{COMPACT_STYLE}</style>
      {preserveRichTextLayout && <style>{CLINICAL_RICH_TEXT_STYLES}</style>}
      {inlineStart && <style>{INLINE_STRIP_STYLE}</style>}
      {hasContent ? (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            pointerEvents: "none",
            ...textStyle,
          }}
          dangerouslySetInnerHTML={{ __html: displayHtml }}
        />
      ) : (
        <Text
          size="xs"
          c="dimmed"
          fs="italic"
          style={{ pointerEvents: "none" }}
        >
          {placeholder}
        </Text>
      )}
    </Box>
  );
});
